import { NgModule, Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap';
import { ProductsService, DiscountsService } from '../../../../../services';
import { PARTNER_OBJECT_TYPE, SAP_PAYMENT_TYPE, SAP_MERCHANT_TYPE, MERCHANT_TYPE, PAYMENT_TYPE, SYSTEM } from '../../../../../enums';
import { finalize } from 'rxjs/operators';
import { PartnerApi, UserDataApi, DiscountRequestApi, DiscountApi, VolumeDiscountApi, VolumeDiscountRateApi, VolumeDiscountRateRequestApi, ConfigurationApi } from '../../../../../../assets/js/lb-sdk';
import { IMyDpOptions, IMyDateModel } from 'mydatepicker';
import { ToastrService } from 'ngx-toastr';
import { LoadingAndErrors } from '../../../../../classes';

interface DiscountForm {
  id?: number,
  creditTerms: number,
  endDate: IMyDateModel,
  partnerId: string,
  paymentType: string,
  merchantType: string,
  productType?: { id: string, name: string },
  rate: number,
  unit: string
}

@Component({
  selector: 'app-discount-request-form',
  templateUrl: './discount-request-form.component.html',
  styleUrls: ['./discount-request-form.component.css']
})
export class DiscountRequestFormComponent extends LoadingAndErrors {
  data: DiscountForm;
  userRole;
  volumeDiscountRates;
  volumeDiscounts;
  match;
  discountRate;
  volumeDiscountRateRequest;
  oldData: DiscountForm;
  paymentTypes: typeof SAP_PAYMENT_TYPE = SAP_PAYMENT_TYPE;
  merchantTypes: typeof SAP_MERCHANT_TYPE = SAP_MERCHANT_TYPE;
  verifiedPartnerId: boolean = false;
  updateMode: boolean = false;
  myDatePickerOptions: IMyDpOptions = {
    disableUntil: {
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      day: new Date().getDate() - 1
    }
  };

  constructor(
    public modal: BsModalRef,
    public productService: ProductsService,
    private partnerApi: PartnerApi,
    private volumeDiscountApi: VolumeDiscountApi,
    private discountsService: DiscountsService,
    private discountRateRequestApi: DiscountRequestApi,
    private volumeDiscountRateApi: VolumeDiscountRateApi,
    private volumeDiscountRateRequestApi: VolumeDiscountRateRequestApi,
    private userDataApi: UserDataApi,
    private toast: ToastrService
  ) {
    super();
    this.discountsService.fetchMaxDiscounts();
  }

  ngOnInit() {
    this.data = this.data || {
      creditTerms: 0,
      endDate: undefined,
      partnerId: undefined,
      merchantType: undefined,
      paymentType: undefined,
      productType: undefined,
      rate: 0,
      unit: 'P'
    };
    if (this.data.partnerId) {
      this.oldData = {
        ...this.data
      };
      this.updateMode = true;
    }
  }

  dismiss(reason: string) {
    this.modal.content.reason = reason;
    this.modal.hide();
  }

  invalidate() {
    this.verifiedPartnerId = false;
  }

  close(form) {
    if (form.valid) {
      const {
        creditTerms,
        endDate,
        partnerId,
        merchantType,
        paymentType,
        productType,
        rate,
        unit
      } = this.data;
      this.clearErrors();
      if (this.verifiedPartnerId || this.updateMode) {
        this.loading.submit = true;
        (this.updateMode ?
          this.discountRateRequestApi
            .patchAttributes(
              this.oldData.id,
              {
                creditTerms,
                endDate: endDate.jsdate,
                partnerId,
                merchantType,
                paymentType,
                productType: productType.name,
                rate,
                unit
              }
            ) :
          this.discountRateRequestApi
            .create({
              creditTerms,
              endDate: endDate.jsdate,
              partnerId,
              merchantType,
              paymentType,
              productType: productType.name,
              rate,
              unit
            })
        )
        .pipe(finalize(() => {
          this.loading.submit = false;
        }))
        .subscribe(
          response => {
            this.modal.content.result = response;
            this.modal.hide();
          },
          error => {
            this.errors.submit = error && error.message || 'Failed to create/update discount rate.';
          }
        );
      } else {
        this.loading.verify = true;
        this.partnerApi
          .exists(partnerId)
          .pipe(finalize(() => {
            this.loading.verify = false;
          }))
          .subscribe((type: any) => {
            if (type.exists == PARTNER_OBJECT_TYPE.MERCHANT) {
              this.loadUserData(partnerId);
            } else {
              this.errors.submit = 'Cannot setup discount rate for non merchant partners.';
            }
          }, error => {
            if (error && error.message && error.message === 'Account already exists.') {
              if (error.type == PARTNER_OBJECT_TYPE.MERCHANT) {
                this.loadUserData(partnerId);
              } else {
                this.errors.submit = 'Cannot setup discount rate for non merchant partners.';
              }
            } else {
              this.errors.submit = error.message;
            }
          });
      }
    }
  }

  loadUserData(merchantId) {
    this.userRole = undefined;
    this.userDataApi
      .findOne({
        where: { partnerId: merchantId },
        include: { roles: 'role' }
      })
      .pipe(finalize(() => {
        this.verifiedPartnerId = true;
      }))
      .subscribe((user: any) => {
        if (user && user.roles && user.roles.length) {
          this.userRole = user.roles[0];
          this.data.merchantType = this.toSapMerchantType(this.userRole.role.code);
          this.data.paymentType = this.toSapPaymentType(this.userRole.paymentType);
          this.fetchVolumeDiscountRates(merchantId, this.userRole.role.code, this.userRole.paymentType);
        } else {
          this.data.merchantType = SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = SAP_PAYMENT_TYPE.PAYMENT_FIRST;
          this.loadDiscountRate(merchantId);
        }
      }, error => {
        this.toast.warning('Partner is not yet registered in salesviewpoint.', 'Merchant Details');
        this.loadDiscountRate(merchantId);
      });
  }

  loadDiscountRate(partnerId) {
    this.loading.discountRates = true;
    this.discountRate = undefined;
    this.partnerApi
      .getDiscount(partnerId, undefined, true)
      .pipe(finalize(() => {
        this.loading.discountRates = false;
      }))
      .subscribe(discountRates => {
        this.discountRate = discountRates && discountRates[0];
        if (this.discountRate) {
          this.data.merchantType = this.discountRate.merchantType || SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = this.discountRate.paymentType || SAP_PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchVolumeDiscountRates(partnerId, this.fromSapMerchantType(this.data.merchantType), this.fromSapPaymentType(this.data.paymentType));
        } else {
          this.loadDiscountRateRequest(partnerId);
        }
      }, error => {
        this.loadDiscountRateRequest(partnerId);
      });
  }

  loadDiscountRateRequest(partnerId) {
    this.loading.discountRateRequest = true;
    this.match = undefined;
    this.discountRateRequestApi
      .findOne({
        where: { partnerId: partnerId }
      })
      .pipe(finalize(() => {
        this.loading.discountRateRequest = false;
      }))
      .subscribe((discountRateRequest: any) => {
        this.match = discountRateRequest;
        if (discountRateRequest) {
          this.data.merchantType = discountRateRequest.merchantType || SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = discountRateRequest.paymentType || SAP_PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchVolumeDiscountRates(partnerId, this.fromSapMerchantType(this.data.merchantType), this.fromSapPaymentType(this.data.paymentType));
        } else {
          this.fetchVolumeDiscountRates(partnerId);
        }
      }, error => {
        this.fetchVolumeDiscountRates(partnerId);
      });
  }

  loadVolumeDiscountRateRequest(partnerId) {
    this.loading.volumeDiscountRateRequest = true;
    this.volumeDiscountRateRequest = undefined;
    this.volumeDiscountRateRequestApi
      .findOne({
        where: { partnerId: partnerId }
      })
      .pipe(finalize(() => {
        this.loading.volumeDiscountRateRequest = false;
      }))
      .subscribe((volumeDiscountRateRequest: any) => {
        this.volumeDiscountRateRequest = volumeDiscountRateRequest;
        this.data.merchantType = this.toSapMerchantType(volumeDiscountRateRequest.merchantType) || SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
        this.data.paymentType = this.toSapPaymentType(volumeDiscountRateRequest.paymentType) || SAP_PAYMENT_TYPE.PAYMENT_FIRST;
        this.fetchVolumeDiscounts(partnerId);
      }, error => {
        this.data.merchantType = this.toSapMerchantType(this.userRole && this.userRole.role && this.userRole.role.code) || SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
        this.data.paymentType = this.toSapPaymentType(this.userRole && this.userRole.paymentType) || SAP_PAYMENT_TYPE.PAYMENT_FIRST;
        this.fetchVolumeDiscounts();
      });
  }

  fetchVolumeDiscountRates(partnerId, merchantType?, paymentType?) {
    this.loading.volumeDiscountRates = true;
    this.clearErrors();
    this.volumeDiscountRateApi
      .find({
        where: { partnerId: partnerId }
      })
      .pipe(finalize(() => {
        this.loading.volumeDiscountRates = false;
      }))
      .subscribe(response => {
        if (merchantType && paymentType) {
          this.volumeDiscountRates = response.filter((discount: any) => {
            return merchantType === discount.merchantType && paymentType === discount.paymentType;
          });
          this.data.merchantType = this.toSapMerchantType(merchantType) || SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
          this.data.paymentType = this.toSapPaymentType(paymentType) || SAP_PAYMENT_TYPE.PAYMENT_FIRST;
          this.fetchVolumeDiscounts(merchantType, paymentType);
        } else {
          this.volumeDiscountRates = response;
          if (this.volumeDiscountRates && this.volumeDiscountRates.length) {
            this.data.merchantType = this.volumeDiscountRates[0] && this.toSapMerchantType(this.volumeDiscountRates[0].merchantType) || SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
            this.data.paymentType = this.volumeDiscountRates[0] && this.toSapPaymentType(this.volumeDiscountRates[0].paymentType) || SAP_PAYMENT_TYPE.PAYMENT_FIRST;
            this.fetchVolumeDiscounts(this.volumeDiscountRates[0].merchantType, this.volumeDiscountRates[0].paymentType)
          } else {
            this.loadVolumeDiscountRateRequest(partnerId);
          }
        }
      },
      error => {
        this.errors.volumeDiscountRates = 'An error occured while fetching the volume discounts.';
      });
  }

  fetchVolumeDiscounts(merchantType?, paymentType?) {
    this.loading.volumeDiscounts = true;
    this.clearErrors();
    this.volumeDiscountApi
      .find({
        where: { paymentType, merchantType }
      })
      .pipe(finalize(() => {
        this.loading.volumeDiscounts = false;
        this.verifiedPartnerId = true;
      }))
      .subscribe(response => {
        this.volumeDiscounts = response;
      },
      error => {
        this.errors.volumeDiscounts = 'An error occured while fetching the volume discounts.';
      });
  }

  getMaxDiscountOfProduct(productType, merchantType, paymentType) {
    if (productType && productType.name && SYSTEM.MAX_DISCOUNT[productType.name]) {
      let discount = (this.volumeDiscountRates || [])
        .filter(discount => {
          return discount.productType === productType.name &&
            discount.merchantType === this.fromSapMerchantType(merchantType) &&
            discount.paymentType === this.fromSapPaymentType(paymentType);
        })
        .sort((a, b) => {
          return b.discount - a.discount;
        })[0];
      if (!discount) {
        discount = (this.volumeDiscounts || [])
          .filter(volumeDiscount => {
            return volumeDiscount.productType === productType.name &&
              volumeDiscount.merchantType === this.fromSapMerchantType(merchantType) &&
              volumeDiscount.paymentType === this.fromSapPaymentType(paymentType);
          })
          .sort((a, b) => {
            return b.discount - a.discount;
          })[0];
      }
      return SYSTEM.MAX_DISCOUNT[productType.name] - parseFloat(discount && discount.discount || 0);
    } else {
      return 100;
    }
  }

  toSapMerchantType(merchantType) {
    switch (merchantType) {
      case MERCHANT_TYPE.DISTRIBUTOR:
        return SAP_MERCHANT_TYPE.DISTRIBUTOR;
      case MERCHANT_TYPE.NATIONAL_RETAILER:
        return SAP_MERCHANT_TYPE.NATIONAL_RETAILER;
      case MERCHANT_TYPE.ONLINE_RETAILER:
        return SAP_MERCHANT_TYPE.ONLINE_RETAILER;
      case MERCHANT_TYPE.GENERAL_MERCHANT:
      default:
        return SAP_MERCHANT_TYPE.GENERAL_MERCHANT;
    }
  }

  toSapPaymentType(paymentType) {
    switch (paymentType) {
      case PAYMENT_TYPE.CREDIT_TERMS:
        return SAP_PAYMENT_TYPE.CREDIT_TERMS;
      case PAYMENT_TYPE.PAYMENT_FIRST:
      default:
        return SAP_PAYMENT_TYPE.PAYMENT_FIRST;
    }
  }

  fromSapMerchantType(merchantType) {
    switch (merchantType) {
      case SAP_MERCHANT_TYPE.DISTRIBUTOR:
        return MERCHANT_TYPE.DISTRIBUTOR;
      case SAP_MERCHANT_TYPE.NATIONAL_RETAILER:
        return MERCHANT_TYPE.NATIONAL_RETAILER;
      case SAP_MERCHANT_TYPE.ONLINE_RETAILER:
        return MERCHANT_TYPE.ONLINE_RETAILER;
      case SAP_MERCHANT_TYPE.GENERAL_MERCHANT:
      default:
        return MERCHANT_TYPE.GENERAL_MERCHANT;
    }
  }

  fromSapPaymentType(paymentType) {
    switch (paymentType) {
      case SAP_PAYMENT_TYPE.CREDIT_TERMS:
        return PAYMENT_TYPE.CREDIT_TERMS;
      case SAP_PAYMENT_TYPE.PAYMENT_FIRST:
      default:
        return PAYMENT_TYPE.PAYMENT_FIRST;
    }
  }
}
